import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import './App.css';
import Login from './components/Login';
import AdminDashboard from './components/AdminDashboard';
import TrainerDashboard from './components/TrainerDashboard';
import TrainerTemplates from './components/TrainerTemplates';
import ClientDetail from './components/ClientDetail';
import ClientDashboard from './components/ClientDashboard';
import NotificationBell from './components/NotificationBell';
import UserSettingsModal from './components/UserSettingsModal';
import ConfirmModal from './components/ConfirmModal';
import FirstTimeConsentModal from './components/FirstTimeConsentModal';
import LegalModal from './components/LegalModal';
import { SSEProvider } from './context/SSEContext';

const API_URL = '/api';

function App() {
  const [auth, setAuth] = useState(() => {
    const saved = localStorage.getItem('training-plan-auth');
    return saved ? JSON.parse(saved) : null;
  });

  const [sessionError, setSessionError] = useState('');
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [showSignoutConfirm, setShowSignoutConfirm] = useState(false);
  const [legalModalOpen, setLegalModalOpen] = useState(false);
  const [legalTab, setLegalTab] = useState('privacy');
  const navigate = useNavigate();
  const location = useLocation();

  const isLoggingOutRef = useRef(false);

  const handleSessionExpired = useCallback((errorMsg = 'Session expired. Please sign in again.') => {
    if (isLoggingOutRef.current) return;
    isLoggingOutRef.current = true;
    localStorage.removeItem('training-plan-auth');
    setAuth(null);
    setSessionError(errorMsg);
    navigate('/login', { replace: true });
    setTimeout(() => {
      isLoggingOutRef.current = false;
    }, 1000);
  }, [navigate]);

  // Global Axios Interceptor for 401 Unauthorized handling
  useEffect(() => {
    const interceptorId = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        const status = error.response?.status;
        const reqUrl = error.config?.url || '';

        // Ignore 401 on login endpoint itself so normal invalid credentials error is shown
        if (status === 401 && !reqUrl.includes('/auth/login')) {
          const errorMsg = error.response?.data?.error || 'Session expired. Please sign in again.';
          handleSessionExpired(errorMsg);
        }
        return Promise.reject(error);
      }
    );

    return () => {
      axios.interceptors.response.eject(interceptorId);
    };
  }, [handleSessionExpired]);

  // Verify auth token on initial load
  useEffect(() => {
    if (!auth?.token) return;

    axios.get(`${API_URL}/auth/me`, {
      headers: { Authorization: `Bearer ${auth.token}` }
    }).catch((err) => {
      if (err.response?.status === 401) {
        handleSessionExpired();
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Silent Token Refresh: Refresh JWT every 12 hours while app is open and user is authenticated
  useEffect(() => {
    if (!auth?.token) return;

    const refreshInterval = setInterval(async () => {
      try {
        const res = await axios.post(`${API_URL}/auth/refresh`, {}, {
          headers: { Authorization: `Bearer ${auth.token}` }
        });
        if (res.data?.access_token) {
          setAuth((prev) => prev ? {
            ...prev,
            token: res.data.access_token,
            user: res.data.user || prev.user
          } : null);
        }
      } catch (err) {
        if (err.response?.status === 401) {
          handleSessionExpired();
        }
      }
    }, 12 * 60 * 60 * 1000); // 12 hours

    return () => clearInterval(refreshInterval);
  }, [auth?.token, handleSessionExpired]);

  useEffect(() => {
    if (auth) {
      localStorage.setItem('training-plan-auth', JSON.stringify(auth));
    } else {
      localStorage.removeItem('training-plan-auth');
    }
  }, [auth]);

  const openLegal = (tab = 'privacy') => {
    setLegalTab(tab);
    setLegalModalOpen(true);
  };

  const handleLogin = (userData) => {
    setSessionError('');
    setAuth(userData);
    const targetPath = (location.pathname && location.pathname !== '/login')
      ? location.pathname + location.search + location.hash
      : '/';
    navigate(targetPath, { replace: true });
  };

  const handleLogoutClick = () => {
    setShowSignoutConfirm(true);
  };

  const handleConfirmLogout = () => {
    setShowSignoutConfirm(false);
    setSessionError('');
    setAuth(null);
    navigate('/', { replace: true });
  };

  const handleCancelLogout = () => {
    setShowSignoutConfirm(false);
  };

  const handleUserUpdated = (updatedUser) => {
    if (auth && updatedUser) {
      setAuth({
        ...auth,
        user: { ...auth.user, ...updatedUser }
      });
    }
  };

  if (!auth) {
    return (
      <Routes>
        <Route path="/login" element={<Login onLogin={handleLogin} initialError={sessionError} />} />
        <Route path="*" element={<Login onLogin={handleLogin} initialError={sessionError} />} />
      </Routes>
    );
  }

  const rawRole = (auth.user?.role || '').toUpperCase();
  const role = rawRole === 'ADMIN' ? 'ADMIN' : (rawRole === 'TRAINER' ? 'TRAINER' : 'CLIENT');

  return (
    <SSEProvider auth={auth}>
      <div className="App">
        <nav className="App-nav">
          <div className="App-nav-inner">
            <div className="nav-controls-right">
              {/* Notification Bell Icon (Hidden for Admin) */}
              {role !== 'ADMIN' && <NotificationBell auth={auth} />}

            {/* Config / Settings Button */}
            <button
              type="button"
              className="nav-settings-button"
              onClick={() => setShowSettingsModal(true)}
              title="Settings & Preferences"
              aria-label="Settings"
            >
              <svg
                className="nav-settings-icon"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <circle cx="12" cy="12" r="3"></circle>
                <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"></path>
              </svg>
            </button>

            <button className="logout-button" onClick={handleLogoutClick}>
              Sign out
            </button>
          </div>
        </div>
      </nav>

      {/* User Settings Modal */}
      <UserSettingsModal
        auth={auth}
        isOpen={showSettingsModal}
        onClose={() => setShowSettingsModal(false)}
        onUpdated={handleUserUpdated}
      />

      {/* Sign out confirmation modal */}
      <ConfirmModal
        isOpen={showSignoutConfirm}
        message="Are you sure you want to sign out?"
        confirmText="Sign out"
        confirmVariant="danger"
        onConfirm={handleConfirmLogout}
        onCancel={handleCancelLogout}
      />

      {/* First-time login GDPR / Terms consent modal */}
      <FirstTimeConsentModal
        isOpen={Boolean(auth && auth.user && auth.user.terms_accepted === false)}
        auth={auth}
        onAccepted={handleUserUpdated}
        onDecline={handleConfirmLogout}
        onOpenLegal={openLegal}
      />

      {/* Legal documents modal (Terms of Service & Privacy Policy) */}
      <LegalModal
        isOpen={legalModalOpen}
        initialTab={legalTab}
        onClose={() => setLegalModalOpen(false)}
      />

      <main className="App-main">
        {role === 'ADMIN' && (
          <Routes>
            <Route path="/" element={<AdminDashboard auth={auth} />} />
            <Route path="/admin" element={<AdminDashboard auth={auth} />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        )}

        {role === 'TRAINER' && (
          <Routes>
            <Route path="/" element={<TrainerDashboard auth={auth} />} />
            <Route path="/trainer" element={<TrainerDashboard auth={auth} />} />
            <Route path="/trainer/templates" element={<TrainerTemplates auth={auth} />} />
            <Route path="/trainer/templates/new" element={<TrainerTemplates auth={auth} />} />
            <Route path="/trainer/templates/:templateId" element={<TrainerTemplates auth={auth} />} />
            <Route path="/trainer/client/:clientId" element={<ClientDetail auth={auth} />} />
            <Route path="/trainer/client/:clientId/workout/:dateStr" element={<ClientDetail auth={auth} />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        )}

        {role === 'CLIENT' && (
          <Routes>
            <Route path="/" element={<ClientDashboard auth={auth} />} />
            <Route path="/client" element={<ClientDashboard auth={auth} />} />
            <Route path="/client/workout/:dateStr" element={<ClientDashboard auth={auth} />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        )}
      </main>
    </div>
    </SSEProvider>
  );
}

export default App;
